/**
 * SettingsContent - Configurações da loja (inclui MercadoPagoSettings e PixWaitPaymentSettings,
 * usados apenas aqui). Extraído do App.js (Fase 2 refactor) — mesmo comportamento, apenas reorganização.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Clock, DollarSign, MapPin, Palette, Printer, Sliders, UserCog, Bell } from 'lucide-react';
import { useAuth } from '../../contexts/AppAuthContext';
import { getBackendUrl } from '../../utils/api';
import { GeneralTab, ScheduleTab, DeliveryTab, AccountTab, AppearanceTab, PaymentTab, ImpressionTab, NotificationSoundSettings } from './settings';

const API = `${getBackendUrl()}/api`;

const SettingsContent = () => {
  const { restaurant } = useAuth();
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // General Settings
  const [generalSettings, setGeneralSettings] = useState({
    name: restaurant?.name || '',
    subdomain: restaurant?.subdomain || '',
    phone: restaurant?.phone || '',
    minimum_order: restaurant?.minimum_order || 15.00,
    delivery_time: restaurant?.delivery_time || ''
  });

  // Schedule Settings
  const [scheduleSettings, setScheduleSettings] = useState({
    auto_open_close: false,
    lunch_break_enabled: false,
    lunch_break_start: '12:00',
    lunch_break_end: '14:00',
    schedule: {
      monday: { enabled: true, open: '10:00', close: '22:00', lunch_break: false },
      tuesday: { enabled: true, open: '10:00', close: '22:00', lunch_break: false },
      wednesday: { enabled: true, open: '10:00', close: '22:00', lunch_break: false },
      thursday: { enabled: true, open: '10:00', close: '22:00', lunch_break: false },
      friday: { enabled: true, open: '10:00', close: '22:00', lunch_break: false },
      saturday: { enabled: true, open: '10:00', close: '23:00', lunch_break: false },
      sunday: { enabled: true, open: '11:00', close: '21:00', lunch_break: false }
    }
  });

  // Scheduling Settings (Agendamento de Pedidos)
  const [schedulingSettings, setSchedulingSettings] = useState({
    scheduling_enabled: false,
    scheduling_when_open: false,
    scheduling_max_days: 7
  });

  // Account Settings
  const [accountSettings, setAccountSettings] = useState({
    email: restaurant?.email || '',
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  // Menu Appearance Settings
  const [appearanceSettings, setAppearanceSettings] = useState({
    primary_color: '#3b82f6',
    secondary_color: '#ef4444',
    layout: 'list', // 'list' or 'grid'
    logo_url: null
  });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);

  // Payment Methods
  const [paymentMethods, setPaymentMethods] = useState({
    dinheiro: true,
    cartao: true,
    pix: true,
    pix_key: '',
    pix_mp: false,
    vale_refeicao: false,
    vale_alimentacao: false,
    cartao_bandeiras: [],
    vale_refeicao_bandeiras: [],
    vale_alimentacao_bandeiras: []
  });

  // Opções de bandeiras disponíveis
  const bandeirasCartao = [
    { id: 'visa', nome: 'Visa' },
    { id: 'mastercard', nome: 'Mastercard' },
    { id: 'elo', nome: 'Elo' },
    { id: 'amex', nome: 'American Express' },
    { id: 'hipercard', nome: 'Hipercard' },
    { id: 'diners', nome: 'Diners Club' }
  ];

  const bandeirasVale = [
    { id: 'sodexo', nome: 'Sodexo' },
    { id: 'alelo', nome: 'Alelo' },
    { id: 'ticket', nome: 'Ticket' },
    { id: 'vr', nome: 'VR' },
    { id: 'ben', nome: 'Ben Visa Vale' },
    { id: 'greencard', nome: 'Green Card' },
    { id: 'cooper', nome: 'Cooper Card' }
  ];

  // Impression Settings
  const [impressionSettings, setImpressionSettings] = useState({
    configured: false,
    active: false,
    token: null,
    print_debounce_seconds: 70
  });
  const [generatingToken, setGeneratingToken] = useState(false);
  const [downloadingConfig, setDownloadingConfig] = useState(false);
  const [tokenCopied, setTokenCopied] = useState(false);
  const [debounceInput, setDebounceInput] = useState(70);
  const [savingDebounce, setSavingDebounce] = useState(false);

  // Personalização do Comprovante de Impressão
  const DEFAULT_PRINT_FORMAT = {
    escpos_enabled: true,
    bold_order_number: true,
    bold_customer_name: false,
    bold_product_name: true,
    bold_additionals: true,
    bold_observations: false,
    bold_total: true,
    show_datetime: true,
    show_phone: true,
    show_address: true,
    show_delivery_fee: true,
    show_subtotal: true,
    show_payment_method: true,
    show_observations: true,
    show_footer: true,
    header_text: '',
    footer_message: '',
    print_mode: 'tradicional',
    font_size: 'normal',
    size_order_number: 'grande',
    size_total: 'grande',
    size_customer_name: 'normal',
    size_items: 'normal',
  };
  const [printFormat, setPrintFormat] = useState(DEFAULT_PRINT_FORMAT);
  const [savingPrintFormat, setSavingPrintFormat] = useState(false);

  const PREVIEW_FONT_PX = { normal: 11, media: 15, grande: 19, gigante: 26 };
  const previewStyle = (sizeKey, boldKey) => ({
    fontSize: (PREVIEW_FONT_PX[printFormat[sizeKey]] || 11) + 'px',
    fontWeight: boldKey && printFormat[boldKey] ? 700 : 400,
    lineHeight: 1.35,
  });

  // Carregar configurações de impressão
  useEffect(() => {
    if (activeTab === 'impression') {
      loadImpressionSettings();
    }
  }, [activeTab]);

  const loadImpressionSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/impression/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setImpressionSettings(response.data);
      const val = Number(response.data?.print_debounce_seconds);
      setDebounceInput(Number.isFinite(val) ? val : 70);
      if (response.data?.print_format) {
        setPrintFormat({ ...DEFAULT_PRINT_FORMAT, ...response.data.print_format });
      }
    } catch (err) {
      console.error('Error loading impression settings:', err);
    }
  };

  const handleSaveDebounce = async () => {
    try {
      setSavingDebounce(true);
      setError('');
      setSuccess('');
      const clamped = Math.max(0, Math.min(300, Math.round(Number(debounceInput) || 0)));
      const token = localStorage.getItem('token');
      await axios.put(`${API}/impression/debounce`, { print_debounce_seconds: clamped }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setImpressionSettings(prev => ({ ...prev, print_debounce_seconds: clamped }));
      setDebounceInput(clamped);
      setSuccess(clamped === 0
        ? 'Debounce desativado - mesas imprimem imediato ao adicionar item.'
        : `Debounce salvo: mesas imprimem ${clamped}s após o último item adicionado.`);
    } catch (err) {
      if (err.response?.status === 404) setError('Gere um token de impressão primeiro');
      else setError('Erro ao salvar tempo de debounce');
    } finally {
      setSavingDebounce(false);
    }
  };

  const togglePrintFormat = (key) => {
    setPrintFormat(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSavePrintFormat = async () => {
    try {
      setSavingPrintFormat(true);
      setError('');
      setSuccess('');
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API}/impression/print-format`, printFormat, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.print_format) {
        setPrintFormat({ ...DEFAULT_PRINT_FORMAT, ...response.data.print_format });
      }
      setSuccess('Personalização do comprovante salva com sucesso! 🎨');
    } catch (err) {
      setError('Erro ao salvar personalização do comprovante');
      console.error(err);
    } finally {
      setSavingPrintFormat(false);
    }
  };

  const handleGenerateToken = async () => {
    try {
      setGeneratingToken(true);
      setError('');
      setSuccess('');
      
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/impression/generate-token`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setImpressionSettings(prev => ({
        ...prev,
        configured: true,
        token: response.data.token,
        active: response.data.active
      }));
      
      setSuccess('Token gerado com sucesso! 🎉');
    } catch (err) {
      setError('Erro ao gerar token de impressão');
      console.error(err);
    } finally {
      setGeneratingToken(false);
    }
  };

  const handleToggleImpression = async () => {
    try {
      setError('');
      setSuccess('');
      
      const newActiveState = !impressionSettings.active;
      const token = localStorage.getItem('token');
      
      await axios.put(`${API}/impression/toggle`, 
        { active: newActiveState },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      setImpressionSettings(prev => ({
        ...prev,
        active: newActiveState
      }));
      
      setSuccess(newActiveState ? 'Impressão automática ativada! 🖨️' : 'Impressão automática desativada');
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Configure um token de impressão primeiro');
      } else {
        setError('Erro ao alternar impressão automática');
      }
      console.error(err);
    }
  };

  const handleDownloadConfig = async () => {
    try {
      setDownloadingConfig(true);
      setError('');
      
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/impression/download-config`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'text'  // Receber como texto puro, não parsear como JSON
      });
      
      // O backend já retorna o JSON no formato correto - NÃO reformatar!
      // Criar blob direto com o conteúdo recebido
      const blob = new Blob([response.data], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'login.json';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setSuccess('Arquivo login.json baixado com sucesso! 📥');
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Gere um token de impressão primeiro');
      } else {
        setError('Erro ao baixar configuração');
      }
      console.error(err);
    } finally {
      setDownloadingConfig(false);
    }
  };

  const copyTokenToClipboard = () => {
    if (impressionSettings.token) {
      navigator.clipboard.writeText(impressionSettings.token);
      setTokenCopied(true);
      setTimeout(() => setTokenCopied(false), 2000);
    }
  };

  const tabs = [
    { id: 'general', label: 'Geral', icon: Sliders },
    { id: 'schedule', label: 'Horários', icon: Clock },
    { id: 'delivery', label: 'Zonas de Entrega', icon: MapPin },
    { id: 'account', label: 'Conta', icon: UserCog },
    { id: 'appearance', label: 'Aparência', icon: Palette },
    { id: 'payment', label: 'Pagamento', icon: DollarSign },
    { id: 'notifications', label: 'Notificações', icon: Bell },
    { id: 'impression', label: 'Impressão Automática', icon: Printer }
  ];

  const handleSaveGeneral = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      await axios.put(`${API}/restaurant/settings`, generalSettings);
      setSuccess('Configurações gerais salvas com sucesso!');
    } catch (err) {
      setError('Erro ao salvar configurações gerais');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSchedule = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      await axios.put(`${API}/restaurant/schedule`, scheduleSettings);
      setSuccess('Horários de funcionamento salvos com sucesso!');
    } catch (err) {
      setError('Erro ao salvar horários');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveScheduling = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      await axios.put(`${API}/restaurant/scheduling`, schedulingSettings);
      setSuccess('Configurações de agendamento salvas com sucesso!');
    } catch (err) {
      setError('Erro ao salvar configurações de agendamento');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAccount = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      if (accountSettings.new_password && accountSettings.new_password !== accountSettings.confirm_password) {
        setError('As senhas não coincidem');
        setLoading(false);
        return;
      }

      await axios.put(`${API}/restaurant/account`, {
        email: accountSettings.email,
        current_password: accountSettings.current_password,
        new_password: accountSettings.new_password
      });
      
      setSuccess('Configurações da conta atualizadas com sucesso!');
      setAccountSettings({ ...accountSettings, current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao atualizar conta');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAppearance = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      await axios.put(`${API}/restaurant/appearance`, appearanceSettings);
      setSuccess('Configurações de aparência salvas com sucesso!');
    } catch (err) {
      setError('Erro ao salvar aparência');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Formato de arquivo não suportado. Use JPG, PNG ou WebP.');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Arquivo muito grande. Tamanho máximo: 5MB');
      return;
    }

    try {
      setUploadingLogo(true);
      setError('');
      setSuccess('');

      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/restaurant/upload-logo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      const logoUrl = response.data.logo_url;
      setAppearanceSettings(prev => ({ ...prev, logo_url: logoUrl }));
      setLogoPreview(logoUrl);
      setSuccess('Logo enviada com sucesso! 🎉');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao enviar logo');
      console.error(err);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!window.confirm('Tem certeza que deseja remover a logo?')) return;

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const token = localStorage.getItem('token');
      await axios.delete(`${API}/restaurant/logo`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setAppearanceSettings(prev => ({ ...prev, logo_url: null }));
      setLogoPreview(null);
      setSuccess('Logo removida com sucesso!');
    } catch (err) {
      setError('Erro ao remover logo');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePayment = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      await axios.put(`${API}/restaurant/payment-methods`, paymentMethods);
      setSuccess('Formas de pagamento atualizadas com sucesso!');
    } catch (err) {
      setError('Erro ao atualizar formas de pagamento');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Load settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await axios.get(`${API}/restaurant/settings`);
        const data = response.data;
        
        setGeneralSettings({
          name: data.name || '',
          subdomain: data.subdomain || '',
          phone: data.phone || '',
          minimum_order: data.minimum_order || 15.00,
          delivery_time: data.delivery_time || ''
        });

        if (data.schedule) {
          setScheduleSettings(data.schedule);
        }

        // Load scheduling settings (agendamento)
        if (data.scheduling) {
          setSchedulingSettings(data.scheduling);
        }

        setAccountSettings({
          ...accountSettings,
          email: data.email || restaurant?.email || ''
        });

        if (data.appearance) {
          setAppearanceSettings(data.appearance);
          if (data.appearance.logo_url) {
            setLogoPreview(data.appearance.logo_url);
          }
        }
        
        // Also check for logo_url directly in restaurant data
        if (data.logo_url) {
          setAppearanceSettings(prev => ({ ...prev, logo_url: data.logo_url }));
          setLogoPreview(data.logo_url);
        }

        if (data.payment_methods) {
          setPaymentMethods(data.payment_methods);
        }
      } catch (err) {
        console.error('Error loading settings:', err);
      }
    };

    loadSettings();
  }, []);

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header - Mobile optimized */}
      <div className="px-1">
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">Configurações</h1>
        <p className="text-sm text-gray-400">Gerencie as configurações do seu estabelecimento</p>
      </div>

      {/* Tabs - Scroll horizontal otimizado para mobile */}
      <div className="relative -mx-4 sm:mx-0">
        <div 
          className="flex space-x-1 sm:space-x-2 border-b border-white/10 overflow-x-auto scrollbar-hide snap-x snap-proximity px-4 sm:px-0 pb-px" 
          style={{ 
            scrollBehavior: 'smooth',
            WebkitOverflowScrolling: 'touch',
            scrollPaddingLeft: '16px',
            scrollPaddingRight: '16px'
          }}
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setError('');
                setSuccess('');
              }}
              className={`flex items-center justify-center space-x-1.5 sm:space-x-2 px-3 sm:px-4 py-3.5 sm:py-3 font-medium transition-all duration-200 relative whitespace-nowrap snap-start min-h-[48px] flex-shrink-0 ${
                activeTab === tab.id
                  ? 'text-blue-400 scale-105'
                  : 'text-gray-400 hover:text-gray-300 active:scale-95'
              }`}
              data-testid={`settings-tab-${tab.id}`}
            >
              <tab.icon size={18} className="w-[18px] h-[18px] flex-shrink-0" />
              <span className="text-xs sm:text-sm font-semibold">{tab.label}</span>
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-400 to-blue-500 rounded-t-full shadow-lg shadow-blue-400/50" />
              )}
            </button>
          ))}
        </div>
        
        {/* Indicadores de scroll mais visíveis para mobile */}
        <div className="absolute top-0 left-0 w-12 h-full bg-gradient-to-r from-gray-900 via-gray-900/80 to-transparent pointer-events-none sm:hidden z-10" />
        <div className="absolute top-0 right-0 w-12 h-full bg-gradient-to-l from-gray-900 via-gray-900/80 to-transparent pointer-events-none sm:hidden z-10" />
      </div>

      {/* Success/Error Messages - Mobile optimized */}
      {success && (
        <div className="bg-green-500/20 border-2 border-green-500/50 text-green-400 px-5 py-4 rounded-xl text-sm sm:text-base font-medium shadow-lg">
          ✓ {success}
        </div>
      )}
      {error && (
        <div className="bg-red-500/20 border-2 border-red-500/50 text-red-400 px-5 py-4 rounded-xl text-sm sm:text-base font-medium shadow-lg">
          ✗ {error}
        </div>
      )}

      {/* General Settings Tab - Mobile optimized */}
      {activeTab === 'general' && <GeneralTab generalSettings={generalSettings} handleSaveGeneral={handleSaveGeneral} loading={loading} setGeneralSettings={setGeneralSettings} />}

      {/* Schedule Settings Tab - Mobile optimized com layout card */}
      {activeTab === 'schedule' && <ScheduleTab handleSaveSchedule={handleSaveSchedule} handleSaveScheduling={handleSaveScheduling} loading={loading} scheduleSettings={scheduleSettings} schedulingSettings={schedulingSettings} setScheduleSettings={setScheduleSettings} setSchedulingSettings={setSchedulingSettings} />}

      {/* Delivery Zones Tab - Configuração de Zonas de Entrega */}
      {activeTab === 'delivery' && <DeliveryTab restaurant={restaurant} setSuccess={setSuccess} />}

      {/* Account Settings Tab - Mobile optimized */}
      {activeTab === 'account' && <AccountTab accountSettings={accountSettings} handleSaveAccount={handleSaveAccount} loading={loading} setAccountSettings={setAccountSettings} />}

      {/* Appearance Settings Tab - Mobile optimized */}
      {activeTab === 'appearance' && <AppearanceTab appearanceSettings={appearanceSettings} handleLogoUpload={handleLogoUpload} handleRemoveLogo={handleRemoveLogo} handleSaveAppearance={handleSaveAppearance} loading={loading} logoPreview={logoPreview} setAppearanceSettings={setAppearanceSettings} uploadingLogo={uploadingLogo} />}

      {/* Payment Methods Tab - Mobile optimized com cards */}
      {activeTab === 'payment' && <PaymentTab bandeirasCartao={bandeirasCartao} bandeirasVale={bandeirasVale} handleSavePayment={handleSavePayment} loading={loading} paymentMethods={paymentMethods} setPaymentMethods={setPaymentMethods} />}

      {/* Notification Sound Tab - Som de novo pedido */}
      {activeTab === 'notifications' && <NotificationSoundSettings />}

      {/* Impressão Automática Tab */}
      {activeTab === 'impression' && <ImpressionTab PREVIEW_FONT_PX={PREVIEW_FONT_PX} copyTokenToClipboard={copyTokenToClipboard} debounceInput={debounceInput} downloadingConfig={downloadingConfig} generatingToken={generatingToken} handleDownloadConfig={handleDownloadConfig} handleGenerateToken={handleGenerateToken} handleSaveDebounce={handleSaveDebounce} handleSavePrintFormat={handleSavePrintFormat} handleToggleImpression={handleToggleImpression} impressionSettings={impressionSettings} previewStyle={previewStyle} printFormat={printFormat} savingDebounce={savingDebounce} savingPrintFormat={savingPrintFormat} setDebounceInput={setDebounceInput} setPrintFormat={setPrintFormat} togglePrintFormat={togglePrintFormat} tokenCopied={tokenCopied} />}
    </div>
  );
};


export default SettingsContent;
